/**
 * @fileoverview API service for interacting with the Google Analytics Filter backend.
 * Provides a set of functions for performing CRUD operations on user-saved filters.
 */
import api from '@api';

/**
 * Creates a new GA filter for the authenticated user.
 * @param {object} options - The function options.
 * @param {string} options.token - The authentication token.
 * @param {object} options.filterData - The filter data to be saved.
 * @param {object} [options.configs={}] - Additional request configs.
 * @returns {Promise<object>} The newly created filter object from the API.
 */
export const createFilter = async ({ token, filterData, configs = {} }) => {
  try {
    const config = {
      ...configs,
      headers: {
        ...(configs?.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    };

    const response = await api.post(
      '/v2/admin/integration/ga-analytics/filters',
      filterData,
      config,
    );
    return response.data?.data;
  } catch (error) {
    console.error(
      'API Error creating filter:',
      error.response?.data || error.message,
    );
    throw new Error(
      error.response?.data?.system?.message || 'Failed to create filter',
    );
  }
};

/**
 * Fetches all saved GA filters for the authenticated user.
 * @param {object} options - The function options.
 * @param {string} options.token - The authentication token.
 * @param {object} [options.configs={}] - Additional request configs.
 * @returns {Promise<Array<object>>} An array of filter objects.
 */
export const fetchFilters = async ({ token, configs = {} }) => {
  try {
    const config = {
      ...configs,
      headers: {
        ...(configs?.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    };

    const response = await api.get(
      '/v2/admin/integration/ga-analytics/filters',
      config,
    );
    return response.data?.data;
  } catch (error) {
    console.error(
      'API Error fetching filters:',
      error.response?.data || error.message,
    );
    throw new Error(
      error.response?.data?.system?.message || 'Failed to fetch filters',
    );
  }
};

/**
 * Fetches a single GA filter by its ID.
 * @param {object} options - The function options.
 * @param {string} options.token - The authentication token.
 * @param {string} options.filterId - The ID of the filter to fetch.
 * @param {object} [options.configs={}] - Additional request configs.
 * @returns {Promise<object>} The filter object.
 */
export const fetchFilterById = async ({ token, filterId, configs = {} }) => {
  try {
    const config = {
      ...configs,
      headers: {
        ...(configs?.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await api.get(
      `/v2/admin/integration/ga-analytics/filters/${filterId}`,
      config,
    );
    return response.data?.data;
  } catch (error) {
    console.error(
      'API Error fetching filter by ID:',
      error.response?.data || error.message,
    );
    throw new Error(
      error.response?.data?.system?.message || 'Failed to fetch filter',
    );
  }
};

/**
 * Updates a GA filter by its ID.
 * @param {object} options - The function options.
 * @param {string} options.token - The authentication token.
 * @param {string} options.filterId - The ID of the filter to update.
 * @param {object} options.updatedData - The updated filter data.
 * @param {object} [options.configs={}] - Additional request configs.
 * @returns {Promise<object>} The updated filter object.
 */
export const updateFilter = async ({
  token,
  filterId,
  updatedData,
  configs = {},
}) => {
  try {
    const config = {
      ...configs,
      headers: {
        ...(configs?.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await api.put(
      `/v2/admin/integration/ga-analytics/filters/${filterId}`,
      updatedData,
      config,
    );
    return response.data?.data;
  } catch (error) {
    console.error(
      'API Error updating filter:',
      error.response?.data || error.message,
    );
    throw new Error(
      error.response?.data?.system?.message || 'Failed to update filter',
    );
  }
};

/**
 * Logically deletes a GA filter by its ID.
 * @param {object} options - The function options.
 * @param {string} options.token - The authentication token.
 * @param {string} options.filterId - The ID of the filter to delete.
 * @param {object} [options.configs={}] - Additional request configs.
 * @returns {Promise<object>} The deleted filter object.
 */
export const deleteFilter = async ({ token, filterId, configs = {} }) => {
  try {
    const config = {
      ...configs,
      headers: {
        ...(configs?.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await api.delete(
      `/v2/admin/integration/ga-analytics/filters/${filterId}`,
      config,
    );
    return response.data?.data;
  } catch (error) {
    console.error(
      'API Error deleting filter:',
      error.response?.data || error.message,
    );
    throw new Error(
      error.response?.data?.system?.message || 'Failed to delete filter',
    );
  }
};
